import React, { useMemo, useState } from "react";
import styled from "styled-components";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Box, Typography, IconButton } from "@mui/material";

/**
 * ballot: [{ id, value, maxLength }]
 * setBallot: fn(newArray)
 * ValidBall: fn(arrayOfValues) -> boolean
 * disabled: boolean
 */
const InputGameSquares = ({ ballot, setBallot, ValidBall, disabled }) => {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(null);

  const options = useMemo(() => {
    const arr = ["B", "I", "N", "G", "O"];
    for (let i = 1; i <= 50; i++) arr.push(String(i));
    return arr;
  }, []);

  const ValidValue = (val) => {
    val = String(val ?? "").toUpperCase();

    let regex = new RegExp("[BINGO0-9]");
    if (!regex.test(val)) val = "";

    let maxLen = 2;

    if (val.length === 1) {
      regex = new RegExp("[BINGO]");
      maxLen = regex.test(val) ? 1 : 2;
      val == 0 ? (val = 1) : (val = val);
    } else if (val.length === 2) {
      regex = new RegExp(/\d\d/, "i");
      if (!regex.test(val)) {
        val = String(val).substr(0, 1);
      }
      val = val < 1 ? 1 : val > 50 ? 50 : val;
    }

    return { val: String(val), maxLen };
  };

  const ValidBox = (item, valu) => {
    const { val, maxLen } = ValidValue(valu);

    const newballs = ballot.map((items) => {
      const newBall = {
        id: items.id,
        value: val,
        maxLength: maxLen,
      };
      return items.id === item.id ? newBall : items;
    });

    setBallot(newballs);
  };

  const AsignBalls = (arr) => {
    const newArr = arr.map((item, idx) => {
      const { val, maxLen } = ValidValue(String(item));
      return {
        id: ballot?.[idx]?.id ?? `${idx}-${Date.now()}`,
        value: val,
        maxLength: maxLen,
      };
    });
    setBallot(newArr);
  };

  const RandomGame = () => {
    let opts = ["B", "I", "N", "G", "O"];
    for (let i = 1; i <= 50; i++) opts.push(i);

    let newVals = [];
    do {
      newVals = ballot.map(() => opts[Math.floor(Math.random() * opts.length)]);
    } while (ValidBall && !ValidBall(newVals));

    AsignBalls(newVals);
  };

  const openPicker = (idx) => {
    if (disabled) return;
    setActiveIdx(idx);
    setOpen(true);
  };

  const closePicker = () => {
    setOpen(false);
    setActiveIdx(null);
  };

  const pickValue = (rawVal) => {
    if (activeIdx === null || activeIdx === undefined) return;

    const next = ballot.map((b, i) => {
      if (i !== activeIdx) return b;
      const { val, maxLen } = ValidValue(String(rawVal));
      return { ...b, value: val, maxLength: maxLen };
    });

    // ✅ Validar repetidos ignorando vacíos
    if (ValidBall) {
      const nonEmptyValues = next.map((x) => x.value).filter((v) => v !== "");
      if (!ValidBall(nonEmptyValues)) return; // evita duplicados reales
    }

    setBallot(next);
    closePicker();
  };

  return (
    <>
      <Row>
        <InputsGrid>
          {ballot.map((item, idx) => (
            <SquareInput
              key={item.id}
              value={item.value}
              placeholder=""
              readOnly
              onClick={() => openPicker(idx)}
              disabled={disabled}
            />
          ))}
        </InputsGrid>

        <AutoBtn
          type="button"
          onClick={RandomGame}
          disabled={disabled}
          aria-label="Jugada automática"
          title="Jugada automática"
        >
          <AutorenewIcon />
        </AutoBtn>
      </Row>

      {/* MODAL SELECTOR */}
      <Modal open={open} onClose={closePicker}>
        <ModalCenter>
          <ModalCard>
            <ModalHeader>
              <Typography className="title">Selecciona una Jugada</Typography>
              <IconButton onClick={closePicker} size="small">
                <CloseIcon />
              </IconButton>
            </ModalHeader>

            {/* Primera fila: BINGO */}
            <OptionsGrid columns={5}>
              {["B", "I", "N", "G", "O"].map((v) => {
                const used = new Set(
                  ballot.map((b) => b.value).filter((v) => v !== "")
                );
                return (
                  <OptionBall
                    key={v}
                    type="button"
                    disabled={used.has(v)}
                    onClick={() => pickValue(v)}
                  >
                    {v}
                  </OptionBall>
                );
              })}
            </OptionsGrid>

            <Divider />

            {/* Números: 1..50 */}
            <OptionsGrid columns={6}>
              {Array.from({ length: 50 }).map((_, i) => {
                const v = String(i + 1);
                const used = new Set(
                  ballot.map((b) => b.value).filter((v) => v !== "")
                );
                return (
                  <OptionBall
                    key={v}
                    type="button"
                    disabled={used.has(v)}
                    onClick={() => pickValue(v)}
                  >
                    {v}
                  </OptionBall>
                );
              })}
            </OptionsGrid>
          </ModalCard>
        </ModalCenter>
      </Modal>
    </>
  );
};

export default InputGameSquares;

/* ===================== styled-components ===================== */

const Row = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: clamp(6px, 2vw, 12px);
  align-items: center;
`;

const InputsGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: clamp(6px, 1.6vw, 10px);
`;

const SquareInput = styled.input`
  width: 100%;
  height: clamp(44px, 8vw, 62px);
  border-radius: clamp(8px, 2vw, 12px);
  border: clamp(3px, 0.9vw, 4px) solid #1a1d28;
  background: #fff;
  text-align: center;
  outline: none;

  font-weight: 900;
  font-size: clamp(16px, 4.8vw, 26px);
  line-height: 1;
  text-transform: uppercase;

  cursor: pointer;

  &:focus {
    box-shadow: 0 0 0 3px rgba(116, 63, 227, 0.25);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.85;
  }
`;

const AutoBtn = styled.button`
  width: clamp(44px, 9vw, 54px);
  height: clamp(44px, 9vw, 54px);
  border: 0;
  border-radius: clamp(10px, 2vw, 12px);
  cursor: pointer;

  background: #743fe3;
  color: #fff;
  display: grid;
  place-items: center;

  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.18);

  svg {
    font-size: clamp(22px, 5vw, 28px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

/* ===== Modal ===== */

const ModalCenter = styled(Box)`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 18px;
`;

const ModalCard = styled.div`
  width: min(560px, 92vw);
  max-height: min(78vh, 640px);
  overflow: auto;

  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.35);
  padding: 18px 18px 16px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;

  .title {
    font-weight: 900;
    font-size: 1.3rem;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 14px 0;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.columns || 6}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 420px) {
    gap: 10px;
    grid-template-columns: repeat(
      ${(p) => Math.max(4, (p.columns || 6) - 1)},
      minmax(0, 1fr)
    );
  }
`;

const OptionBall = styled.button`
  width: 100%;
  aspect-ratio: 1 / 1;
  border: 0;
  border-radius: 999px;
  cursor: pointer;

  background: #f3c623;
  color: #111;
  font-weight: 900;
  font-size: 1.05rem;

  display: grid;
  place-items: center;

  box-shadow: 0 10px 16px rgba(0, 0, 0, 0.18);

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;
